// src/modules/competition/components/CompetitionCard/index.js
import React from 'react';
import { Badge, ProgressBar } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { format, formatDistanceToNow } from 'date-fns';
import PropTypes from 'prop-types';
import './styles.css';

const CompetitionCard = ({ competition }) => {
    const { state, isUserJoined, pointTarget, competitors, ministerOfActivity, winner, winnerName } = competition;
    const startDate = new Date(competition.startDate);
    const endDate = new Date(competition.endDate);
    const now = new Date();
    const participantCount = competition.participantCount || competitors?.length || 0;

    const userPoints = competition.userPoints || 0;
    const userProgress = pointTarget
        ? Math.min((userPoints / pointTarget) * 100, 100)
        : 0;
    const isAboveThreshold = userPoints >= pointTarget;

    const getStatusBadge = () => {
        if (state === 'active') return <Badge bg="success">Active</Badge>;
        if (state === 'upcoming') return <Badge bg="primary">Upcoming</Badge>;
        if (state === 'concluded') return <Badge bg="secondary">Concluded</Badge>;
        return null;
    };

    const getDateInfo = () => {
        if (state === 'active') {
            return `Ends ${formatDistanceToNow(endDate, { addSuffix: true })}`;
        }
        if (state === 'upcoming') {
            return `Starts ${format(startDate, 'MMM d, yyyy')}`;
        }
        if (state === 'concluded') {
            return `Ended ${format(endDate, 'MMM d, yyyy')}`;
        }
        return '';
    };

    return (
        <Link
            to={`/competition/${competition.competitionId}`}
            className={`competition-card competition-card-${state}`}
        >
            <div className="competition-card-header">
                <div className="competition-card-title">
                    <h6 className="competition-name">{competition.name}</h6>
                    {getStatusBadge()}
                    {isUserJoined && <Badge bg="light" text="dark">Joined</Badge>}
                </div>
                <span className="competition-date">{getDateInfo()}</span>
            </div>

            {state === 'active' && isUserJoined && (
                <div className="competition-card-progress">
                    <ProgressBar
                        now={userProgress}
                        variant={isAboveThreshold ? "success" : "primary"}
                        className="competition-progress-bar"
                    />
                    <div className="competition-card-stats">
                        <div className="comp-stat">
                            <span className="comp-stat-value">{userPoints?.toFixed(1) || 0}</span>
                            <span className="comp-stat-label">Points</span>
                        </div>
                        <div className="comp-stat">
                            <span className="comp-stat-value">{pointTarget?.toFixed(0) || 0}</span>
                            <span className="comp-stat-label">Target</span>
                        </div>
                        <div className="comp-stat">
                            <span className="comp-stat-value">{participantCount}</span>
                            <span className="comp-stat-label">Competitors</span>
                        </div>
                    </div>
                </div>
            )}

            {state === 'active' && !isUserJoined && (
                <div className="competition-card-progress">
                    <div className="competition-card-stats">
                        <div className="comp-stat">
                            <span className="comp-stat-value">{pointTarget?.toFixed(0) || 0}</span>
                            <span className="comp-stat-label">Target</span>
                        </div>
                        <div className="comp-stat">
                            <span className="comp-stat-value">{participantCount}</span>
                            <span className="comp-stat-label">Competitors</span>
                        </div>
                    </div>
                    <div className="competition-card-join-cta">
                        <span className="join-cta">Join this competition</span>
                    </div>
                </div>
            )}

            {state === 'concluded' && (
                <div className="competition-card-result">
                    {(winnerName || winner) && (
                        <div className="competition-card-winner">
                            <i className="bi bi-trophy-fill text-warning me-2"></i>
                            <span>Winner: {winnerName || winner}</span>
                        </div>
                    )}
                    <span className="concluded-participants">
                        <i className="bi bi-people me-1"></i>
                        {participantCount} competitors
                    </span>
                </div>
            )}

            {state === 'upcoming' && (
                <div className="competition-card-join">
                    <span className="join-cta">
                        {isUserJoined ? "You're signed up" : "Sign up early"}
                    </span>
                    <span className="join-info">
                        <span className="join-target">Target: {pointTarget?.toFixed(0)} pts</span>
                        <span className="join-participants">
                            <i className="bi bi-people me-1"></i>
                            {participantCount} joined
                        </span>
                    </span>
                </div>
            )}
        </Link>
    );
};

CompetitionCard.propTypes = {
    competition: PropTypes.shape({
        competitionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        name: PropTypes.string.isRequired,
        state: PropTypes.oneOf(['active', 'upcoming', 'concluded']).isRequired,
        startDate: PropTypes.string.isRequired,
        endDate: PropTypes.string.isRequired,
        pointTarget: PropTypes.number,
        competitors: PropTypes.array,
        participantCount: PropTypes.number,
        isUserJoined: PropTypes.bool,
        winner: PropTypes.string,
        ministerOfActivity: PropTypes.shape({
            userGivenName: PropTypes.string
        })
    }).isRequired
};

export default CompetitionCard;
